import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import { ToggleSwitch } from '../../components/ToggleSwitch'

interface Category {
  id: string
  name: string
}

interface Product {
  id: string
  name: string
  description?: string
  price: number
  cost_price?: number
  sku?: string
  image_url?: string
  is_available: boolean
  stock_quantity?: number
  min_stock?: number
  category_id?: string
}

interface ProductForm {
  name: string
  description: string
  price: string
  cost_price: string
  sku: string
  category_id: string
  image_url: string
  stock_quantity: string
  min_stock: string
}

const EMPTY_FORM: ProductForm = {
  name: '',
  description: '',
  price: '',
  cost_price: '',
  sku: '',
  category_id: '',
  image_url: '',
  stock_quantity: '0',
  min_stock: '0',
}

function stockBadge(stock?: number, min?: number) {
  const s = stock ?? 0
  const m = min ?? 0
  if (s === 0) return <span className="badge badge-red">Agotado</span>
  if (s <= m) return <span className="badge badge-yellow">{s} (bajo)</span>
  return <span className="badge badge-green">{s}</span>
}

function marginBadge(price: number, cost?: number) {
  if (!cost || cost <= 0) return null
  const pct = ((price - cost) / price * 100)
  return (
    <span className="badge badge-green" style={{ fontSize: '.7rem' }}>
      {pct.toFixed(1)}%
    </span>
  )
}

export function StoreProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<ProductForm>(EMPTY_FORM)

  // Stock adjustment state
  const [adjustingId, setAdjustingId] = useState<string | null>(null)
  const [adjustQty, setAdjustQty] = useState(0)

  function loadProducts() {
    return api.get<Product[]>('/api/store/products')
      .then(setProducts)
      .catch(err => setError(err.message))
  }

  useEffect(() => {
    Promise.all([
      loadProducts(),
      api.get<Category[]>('/api/admin/categories').catch(() => []),
    ])
      .then(([, cats]) => setCategories(cats as Category[]))
      .finally(() => setLoading(false))
  }, [])

  function openCreate() {
    setEditId(null)
    setForm(EMPTY_FORM)
    setShowForm(true)
  }

  function openEdit(p: Product) {
    setEditId(p.id)
    setForm({
      name: p.name,
      description: p.description ?? '',
      price: String(p.price),
      cost_price: p.cost_price ? String(p.cost_price) : '',
      sku: p.sku ?? '',
      category_id: p.category_id ?? '',
      image_url: p.image_url ?? '',
      stock_quantity: String(p.stock_quantity ?? 0),
      min_stock: String(p.min_stock ?? 0),
    })
    setShowForm(true)
  }

  function cancelForm() {
    setShowForm(false)
    setEditId(null)
    setForm(EMPTY_FORM)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        price: parseFloat(form.price),
        cost_price: form.cost_price ? parseFloat(form.cost_price) : undefined,
        sku: form.sku || undefined,
        category_id: form.category_id || undefined,
        image_url: form.image_url || undefined,
        stock_quantity: parseInt(form.stock_quantity) || 0,
        min_stock: parseInt(form.min_stock) || 0,
      }
      if (editId) {
        await api.patch(`/api/store/products/${editId}`, payload)
      } else {
        await api.post('/api/store/products', payload)
      }
      await loadProducts()
      cancelForm()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('¿Eliminar este producto?')) return
    try {
      await api.delete(`/api/store/products/${id}`)
      setProducts(ps => ps.filter(p => p.id !== id))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    }
  }

  async function toggleAvailability(p: Product) {
    const newVal = !p.is_available
    setProducts(ps => ps.map(x => x.id === p.id ? { ...x, is_available: newVal } : x))
    try {
      await api.patch(`/api/store/products/${p.id}/availability`, { is_available: newVal })
    } catch {
      setProducts(ps => ps.map(x => x.id === p.id ? { ...x, is_available: p.is_available } : x))
    }
  }

  async function applyStockAdjust(productId: string) {
    if (adjustQty === 0) { setAdjustingId(null); return }
    try {
      await api.post(`/api/store/inventory/${productId}/movement`, {
        type: 'ajuste',
        quantity: adjustQty,
        notes: 'Ajuste manual',
      })
      setProducts(ps => ps.map(p =>
        p.id === productId
          ? { ...p, stock_quantity: Math.max(0, (p.stock_quantity ?? 0) + adjustQty) }
          : p
      ))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al ajustar stock')
    } finally {
      setAdjustingId(null)
      setAdjustQty(0)
    }
  }

  if (loading) return <div className="loading-state">Cargando productos...</div>

  return (
    <div className="animate-in">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Productos</h1>
          <p className="admin-sub">{products.length} producto{products.length !== 1 ? 's' : ''} en tu catálogo</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          + Nuevo producto
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Modal form */}
      {showForm && (
        <div className="modal-overlay" onClick={cancelForm}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{editId ? 'Editar producto' : 'Nuevo producto'}</h3>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="prod-name">Nombre *</label>
                  <input id="prod-name" type="text" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="field">
                  <label htmlFor="prod-sku">SKU</label>
                  <input id="prod-sku" type="text" value={form.sku}
                    onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="prod-cat">Categoría</label>
                  <select id="prod-cat" value={form.category_id}
                    onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                    <option value="">— Sin categoría —</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="prod-desc">Descripción</label>
                <input id="prod-desc" type="text" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="prod-price">Precio (Q) *</label>
                  <input id="prod-price" type="number" min="0" step="0.01" value={form.price}
                    onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required />
                </div>
                <div className="field">
                  <label htmlFor="prod-cost">Costo (Q)</label>
                  <input id="prod-cost" type="number" min="0" step="0.01" value={form.cost_price}
                    onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="prod-img">URL imagen</label>
                  <input id="prod-img" type="url" value={form.image_url}
                    onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                    placeholder="https://res.cloudinary.com/..." />
                </div>
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="prod-stock">Stock inicial</label>
                  <input id="prod-stock" type="number" min="0" value={form.stock_quantity}
                    onChange={e => setForm(f => ({ ...f, stock_quantity: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="prod-minstock">Stock mínimo</label>
                  <input id="prod-minstock" type="number" min="0" value={form.min_stock}
                    onChange={e => setForm(f => ({ ...f, min_stock: e.target.value }))} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={cancelForm}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar producto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <p>Aún no tienes productos. Crea el primero.</p>
        </div>
      ) : (
        <div className="products-table">
          <div className="products-table-header">
            <span>Producto</span>
            <span>SKU</span>
            <span>Precio</span>
            <span>Costo</span>
            <span>Margen</span>
            <span>Stock</span>
            <span>Disponible / Acciones</span>
          </div>
          {products.map(p => (
            <div key={p.id} className="products-table-row">
              <div className="product-row-name">
                <strong>{p.name}</strong>
                {p.description && <span className="product-row-desc">{p.description.slice(0, 60)}</span>}
              </div>
              <span className="product-row-sku">{p.sku ?? '—'}</span>
              <span className="product-row-price">Q {Number(p.price).toFixed(2)}</span>
              <span style={{ color: 'var(--muted)', fontSize: '.85rem' }}>
                {p.cost_price ? `Q ${Number(p.cost_price).toFixed(2)}` : '—'}
              </span>
              <span>{marginBadge(p.price, p.cost_price) ?? <span style={{ color: 'var(--muted-2)' }}>—</span>}</span>
              {/* Stock quick adjust */}
              <div>
                {adjustingId === p.id ? (
                  <div className="stock-adjust">
                    <button type="button" className="stock-btn"
                      onClick={() => setAdjustQty(q => q - 1)}>−</button>
                    <span className="stock-value" style={{ color: adjustQty < 0 ? 'var(--red)' : adjustQty > 0 ? 'var(--green)' : 'var(--ink)' }}>
                      {adjustQty > 0 ? `+${adjustQty}` : adjustQty}
                    </span>
                    <button type="button" className="stock-btn"
                      onClick={() => setAdjustQty(q => q + 1)}>+</button>
                    <button type="button" className="btn btn-primary btn-sm"
                      onClick={() => applyStockAdjust(p.id)}>OK</button>
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => { setAdjustingId(null); setAdjustQty(0) }}>✕</button>
                  </div>
                ) : (
                  <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => { setAdjustingId(p.id); setAdjustQty(0) }}>
                    {stockBadge(p.stock_quantity, p.min_stock)}
                    <span style={{ fontSize: '.75rem', color: 'var(--muted-2)' }}>✏️</span>
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <ToggleSwitch
                  checked={p.is_available}
                  onChange={() => toggleAvailability(p)}
                  size="sm"
                />
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Editar</button>
                <button type="button" className="btn btn-ghost btn-sm btn-danger"
                  onClick={() => handleDelete(p.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
